import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { taskAPI, userAPI, projectAPI } from '../services/api';
import toast from 'react-hot-toast';
import {
  FiUser, FiAlertTriangle, FiCheckCircle, FiClock,
  FiTrendingUp, FiFilter, FiRefreshCw, FiAlertCircle
} from 'react-icons/fi';

const PCOL = { low: '#22c55e', medium: '#f59e0b', high: '#ef4444', critical: '#7c3aed' };
const SCOL = {
  todo:        { bg: '#f1f5f9', txt: '#475569' },
  'in-progress':{ bg: '#fef3c7', txt: '#92400e' },
  review:      { bg: '#e0e7ff', txt: '#3730a3' },
  completed:   { bg: '#d1fae5', txt: '#065f46' },
  blocked:     { bg: '#fee2e2', txt: '#991b1b' },
};

export default function Workload() {
  const [users,   setUsers]   = useState([]);
  const [tasks,   setTasks]   = useState([]);
  const [projects,setProjects]= useState([]);
  const [loading, setLoading] = useState(true);
  const [filterProject, setFilterProject] = useState('all');
  const [filterStatus,  setFilterStatus]  = useState('active'); // active | all
  const [view, setView] = useState('cards'); // cards | table | heatmap

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const [uR, tR, pR] = await Promise.all([
        userAPI.getAll(),
        taskAPI.getAll(),
        projectAPI.getAll(),
      ]);
      setUsers(uR.data.users?.filter(u => u.role !== 'client') || []);
      setTasks(tR.data.tasks || []);
      setProjects(pR.data.projects || []);
    } catch { toast.error('Failed to load workload data'); }
    setLoading(false);
  };

  // ── Compute per-user metrics ────────────────────────────────────────────────
  const memberData = users.map(u => {
    let allUserTasks = tasks.filter(t => {
      if (!t.assignedTo) return false;
      if (Array.isArray(t.assignedTo)) {
        return t.assignedTo.some(a => (a?._id || a) === u._id);
      }
      return t.assignedTo?._id === u._id || t.assignedTo === u._id;
    });
    if (filterProject !== 'all')
      allUserTasks = allUserTasks.filter(t => (t.project?._id || t.project) === filterProject);

    // Compute completion rate BEFORE filtering active-only (otherwise it's always 0)
    const totalAll     = allUserTasks.length;
    const completedAll = allUserTasks.filter(t => t.status === 'completed').length;
    const completionRate = totalAll > 0 ? Math.round((completedAll / totalAll) * 100) : 0;

    let userTasks = allUserTasks;
    if (filterStatus === 'active')
      userTasks = userTasks.filter(t => t.status !== 'completed');

    const byStatus = {
      todo:         userTasks.filter(t => t.status === 'todo').length,
      'in-progress':userTasks.filter(t => t.status === 'in-progress').length,
      review:       userTasks.filter(t => t.status === 'review').length,
      completed:    userTasks.filter(t => t.status === 'completed').length,
      blocked:      userTasks.filter(t => t.status === 'blocked').length,
    };

    const estHours    = userTasks.reduce((s,t)=>s+(parseFloat(t.estimatedHours)||0),0);
    const criticalCnt = userTasks.filter(t=>t.priority==='critical'||t.priority==='high').length;
    const overdue     = userTasks.filter(t=>t.dueDate&&new Date(t.dueDate)<new Date()&&t.status!=='completed').length;
    const total       = userTasks.length;

    // Load score: 0-100 based on active tasks + estimated hours
    const loadScore = Math.min(100, Math.round(
      (byStatus['in-progress'] * 15) +
      (byStatus.todo * 8) +
      (byStatus.review * 10) +
      (overdue * 20) +
      (criticalCnt * 12)
    ));

    const loadLevel = loadScore < 25 ? 'light' : loadScore < 55 ? 'moderate' : loadScore < 80 ? 'heavy' : 'overloaded';
    const loadColor = { light:'#22c55e', moderate:'#f59e0b', heavy:'#ef4444', overloaded:'#7c3aed' }[loadLevel];

    const projectCount = new Set(allUserTasks.map(t => t.project?._id || t.projectId).filter(Boolean)).size;

    return { user: u, tasks: userTasks, byStatus, estHours, criticalCnt, overdue, loadScore, loadLevel, loadColor, completionRate, total, projectCount };
  });

  // Unassigned tasks
  const unassigned = tasks.filter(t => {
    const hasAssignee = t.assignedTo && (Array.isArray(t.assignedTo) ? t.assignedTo.length > 0 : true);
    const notCompleted = t.status !== 'completed';
    const matchProject = filterProject === 'all' || (t.project?._id || t.project) === filterProject;
    return !hasAssignee && notCompleted && matchProject;
  });

  const totalActive   = memberData.reduce((s,m)=>s+m.byStatus['in-progress']+m.byStatus.todo,0);
  const overloaded    = memberData.filter(m=>m.loadLevel==='overloaded'||m.loadLevel==='heavy').length;
  const avgLoad       = memberData.length ? Math.round(memberData.reduce((s,m)=>s+m.loadScore,0)/memberData.length) : 0;

  const S = {
    page: { maxWidth: 1280, margin: '0 auto', fontFamily: "'Sora',sans-serif" },
    card: { background:'#fff', borderRadius:16, padding:24, boxShadow:'0 2px 12px #0000000a', border:'1px solid #f1f5f9' },
    btn:  (v,sm) => ({ padding:sm?'7px 14px':'9px 18px', borderRadius:10, border:'none', fontWeight:600, fontSize:sm?12:13, cursor:'pointer', display:'inline-flex', alignItems:'center', gap:6, transition:'all .2s',
      ...(v==='primary'?{background:'var(--pm-gradient)',color:'#fff'}:
         v==='active'?{background:'var(--pm-primary)',color:'#fff'}:
         {background:'#f8fafc',color:'#64748b',border:'1.5px solid #e2e8f0'}) }),
  };

  if (loading) return (
    <div style={{ display:'flex',alignItems:'center',justifyContent:'center',height:'60vh' }}>
      <div style={{ width:40,height:40,border:'3px solid #e2e8f0',borderTop:'3px solid #6366f1',borderRadius:'50%',animation:'spin 1s linear infinite' }}/>
    </div>
  );

  return (
    <div style={S.page}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;600;700;800&display=swap');
        @keyframes spin{to{transform:rotate(360deg)}}
        .wl-card:hover{transform:translateY(-2px);box-shadow:0 8px 24px #0000001a!important;}
        .wl-taskrow:hover{background:#f8fafc;}
      `}</style>

      {/* Header */}
      <div style={{ display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:24,flexWrap:'wrap',gap:12 }}>
        <div>
          <h1 style={{ margin:0,fontSize:28,fontWeight:800,color:'#0f172a' }}>Team Workload</h1>
          <p style={{ margin:'4px 0 0',color:'#64748b',fontSize:14 }}>See how work is distributed across the team</p>
        </div>
        <div style={{ display:'flex',gap:8,flexWrap:'wrap' }}>
          <button style={S.btn('ghost',true)} onClick={load}><FiRefreshCw size={13}/> Refresh</button>
          {/* View toggle */}
          <div style={{ display:'flex',background:'#f1f5f9',borderRadius:10,padding:3 }}>
            {['cards','table','heatmap'].map(v=>(
              <button key={v} onClick={()=>setView(v)} style={{ padding:'6px 14px',borderRadius:8,border:'none',fontWeight:600,fontSize:12,cursor:'pointer',
                background:view===v?'#fff':'transparent',color:view===v?'var(--pm-primary)':'#64748b',boxShadow:view===v?'0 1px 4px #0000001a':'none',transition:'all .2s' }}>
                {v.charAt(0).toUpperCase()+v.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div style={{ display:'flex',gap:12,marginBottom:24,flexWrap:'wrap',alignItems:'center' }}>
        <div style={{ display:'flex',alignItems:'center',gap:6,color:'#64748b',fontSize:13,fontWeight:600 }}>
          <FiFilter size={13}/> Filters:
        </div>
        <select value={filterProject} onChange={e=>setFilterProject(e.target.value)}
          style={{ padding:'7px 12px',borderRadius:9,border:'1.5px solid #e2e8f0',fontSize:13,color:'#475569',background:'#fff',cursor:'pointer' }}>
          <option value="all">All Projects</option>
          {projects.map(p=><option key={p._id} value={p._id}>{p.name}</option>)}
        </select>
        <div style={{ display:'flex',background:'#f1f5f9',borderRadius:10,padding:3 }}>
          {[['active','Active Only'],['all','Include Done']].map(([val,lbl])=>(
            <button key={val} onClick={()=>setFilterStatus(val)} style={{ padding:'6px 14px',borderRadius:8,border:'none',fontWeight:600,fontSize:12,cursor:'pointer',
              background:filterStatus===val?'#fff':'transparent',color:filterStatus===val?'var(--pm-primary)':'#64748b',boxShadow:filterStatus===val?'0 1px 4px #0000001a':'none',transition:'all .2s' }}>
              {lbl}
            </button>
          ))}
        </div>
      </div>

      {/* KPI Strip */}
      <div style={{ display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(180px,1fr))',gap:16,marginBottom:24 }}>
        {[
          { icon:<FiUser/>,     label:'Team Members',    value:users.length,          color:'var(--pm-primary)' },
          { icon:<FiClock/>,    label:'Active Tasks',    value:totalActive,            color:'#f59e0b' },
          { icon:<FiAlertTriangle/>, label:'Overloaded', value:overloaded,             color:'#ef4444' },
          { icon:<FiAlertCircle/>,   label:'Unassigned', value:unassigned.length,      color:'#f97316' },
          { icon:<FiTrendingUp/>,    label:'Avg Load Score', value:`${avgLoad}/100`,   color:'var(--pm-secondary)' },
        ].map(s=>(
          <div key={s.label} style={{ ...S.card, borderLeft:`4px solid ${s.color}`, padding:'18px 20px' }}>
            <div style={{ color:s.color,fontSize:20,marginBottom:8 }}>{s.icon}</div>
            <div style={{ fontSize:26,fontWeight:800,color:'#0f172a' }}>{s.value}</div>
            <div style={{ fontSize:12,color:'#64748b',marginTop:2 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* ── CARDS VIEW ── */}
      {view === 'cards' && (
        <div style={{ display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(320px,1fr))',gap:20,marginBottom:24 }}>
          {memberData.map(m => (
            <div key={m.user._id} className="wl-card" style={{ ...S.card, transition:'all .2s', cursor:'default' }}>
              {/* Member header */}
              <div style={{ display:'flex',alignItems:'center',gap:14,marginBottom:18 }}>
                <div style={{ width:48,height:48,borderRadius:'50%',background:`linear-gradient(135deg,${m.loadColor}88,${m.loadColor})`,color:'#fff',display:'flex',alignItems:'center',justifyContent:'center',fontWeight:800,fontSize:20,flexShrink:0 }}>
                  {m.user.name?.charAt(0).toUpperCase()}
                </div>
                <div style={{ flex:1,minWidth:0 }}>
                  <div style={{ fontWeight:700,fontSize:15,color:'#0f172a' }}>{m.user.name}</div>
                  <div style={{ fontSize:12,color:'#64748b' }}>{m.user.role} · {m.user.email}</div>
                </div>
                <div style={{ textAlign:'right' }}>
                  <div style={{ fontWeight:800,fontSize:18,color:m.loadColor }}>{m.loadScore}</div>
                  <div style={{ fontSize:10,color:'#94a3b8',fontWeight:600,textTransform:'uppercase' }}>load</div>
                </div>
              </div>

              {/* Load bar */}
              <div style={{ marginBottom:16 }}>
                <div style={{ display:'flex',justifyContent:'space-between',marginBottom:4,fontSize:12 }}>
                  <span style={{ color:'#64748b',fontWeight:600,textTransform:'capitalize' }}>{m.loadLevel}</span>
                  <span style={{ color:m.loadColor,fontWeight:700 }}>{m.loadScore}/100</span>
                </div>
                <div style={{ height:8,background:'#f1f5f9',borderRadius:4,overflow:'hidden' }}>
                  <div style={{ height:'100%',width:`${m.loadScore}%`,background:m.loadColor,borderRadius:4,transition:'width .8s' }}/>
                </div>
              </div>

              {/* Task status pills */}
              <div style={{ display:'flex',gap:6,flexWrap:'wrap',marginBottom:16 }}>
                {Object.entries(m.byStatus).filter(([,v])=>v>0).map(([status,count])=>(
                  <span key={status} style={{ ...SCOL[status],padding:'3px 10px',borderRadius:10,fontSize:11,fontWeight:700 }}>
                    {count} {status}
                  </span>
                ))}
                {m.total === 0 && <span style={{ color:'#94a3b8',fontSize:12 }}>No tasks assigned</span>}
              </div>

              {/* Metrics row */}
              <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr 1fr 1fr',gap:8,marginBottom:16 }}>
                {[
                  { label:'Projects',   value:m.projectCount,            color:'var(--pm-secondary)' },
                  { label:'Est Hours',  value:`${m.estHours}h`,          color:'var(--pm-primary)' },
                  { label:'Overdue',    value:m.overdue,                  color: m.overdue>0?'#ef4444':'#22c55e' },
                  { label:'High Pri',   value:m.criticalCnt,              color: m.criticalCnt>2?'#ef4444':'#f59e0b' },
                ].map(x=>(
                  <div key={x.label} style={{ background:'#f8fafc',borderRadius:8,padding:'8px 10px',textAlign:'center' }}>
                    <div style={{ fontSize:16,fontWeight:800,color:x.color }}>{x.value}</div>
                    <div style={{ fontSize:10,color:'#94a3b8',fontWeight:600 }}>{x.label}</div>
                  </div>
                ))}
              </div>

              {/* Warnings */}
              {m.overdue > 0 && (
                <div style={{ display:'flex',alignItems:'center',gap:6,fontSize:12,color:'#ef4444',background:'#fee2e2',borderRadius:8,padding:'7px 10px',marginBottom:8 }}>
                  <FiAlertTriangle size={12}/> {m.overdue} overdue task{m.overdue>1?'s':''}
                </div>
              )}
              {m.loadLevel === 'overloaded' && (
                <div style={{ display:'flex',alignItems:'center',gap:6,fontSize:12,color:'#7c3aed',background:'#ede9fe',borderRadius:8,padding:'7px 10px',marginBottom:8 }}>
                  <FiAlertCircle size={12}/> Overloaded — consider rebalancing
                </div>
              )}

              {/* Tasks preview */}
              {m.tasks.filter(t=>t.status!=='completed').slice(0,3).map(t=>(
                <Link to={`/tasks/${t._id}`} key={t._id} style={{ textDecoration:'none' }}>
                  <div className="wl-taskrow" style={{ display:'flex',alignItems:'center',gap:8,padding:'7px 6px',borderRadius:8,transition:'background .15s' }}>
                    <div style={{ width:7,height:7,borderRadius:'50%',background:PCOL[t.priority],flexShrink:0 }}/>
                    <span style={{ fontSize:12,color:'#0f172a',flex:1,minWidth:0,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap' }}>{t.title}</span>
                    {t.dueDate && (
                      <span style={{ fontSize:10,color: new Date(t.dueDate)<new Date()?'#ef4444':'#94a3b8',flexShrink:0 }}>
                        {new Date(t.dueDate).toLocaleDateString('en-IN',{day:'2-digit',month:'short'})}
                      </span>
                    )}
                  </div>
                </Link>
              ))}
              {m.tasks.filter(t=>t.status!=='completed').length > 3 && (
                <div style={{ fontSize:11,color:'#94a3b8',marginTop:6,paddingLeft:6 }}>
                  +{m.tasks.filter(t=>t.status!=='completed').length-3} more tasks
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* ── TABLE VIEW ── */}
      {view === 'table' && (
        <div style={{ ...S.card,padding:0,overflow:'hidden',marginBottom:24 }}>
          <table style={{ width:'100%',borderCollapse:'collapse',fontSize:13 }}>
            <thead>
              <tr style={{ background:'#f8fafc' }}>
                {['Member','Load','Projects','Active','In Progress','Review','Overdue','High Pri','Est Hours','Completion'].map(h=>(
                  <th key={h} style={{ padding:'12px 16px',textAlign:'left',fontWeight:700,color:'#64748b',borderBottom:'2px solid #f1f5f9',whiteSpace:'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {memberData.map((m,i)=>(
                <tr key={m.user._id} style={{ borderBottom:'1px solid #f8fafc',background:i%2===0?'#fff':'#fafbfc' }}>
                  <td style={{ padding:'12px 16px' }}>
                    <div style={{ display:'flex',alignItems:'center',gap:10 }}>
                      <div style={{ width:32,height:32,borderRadius:'50%',background:`${m.loadColor}22`,color:m.loadColor,display:'flex',alignItems:'center',justifyContent:'center',fontWeight:700,fontSize:13,flexShrink:0 }}>
                        {m.user.name?.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <div style={{ fontWeight:700,color:'#0f172a' }}>{m.user.name}</div>
                        <div style={{ fontSize:11,color:'#94a3b8',textTransform:'capitalize' }}>{m.user.role}</div>
                      </div>
                    </div>
                  </td>
                  <td style={{ padding:'12px 16px' }}>
                    <div style={{ display:'flex',alignItems:'center',gap:8 }}>
                      <div style={{ width:60,height:6,background:'#f1f5f9',borderRadius:3,overflow:'hidden' }}>
                        <div style={{ height:'100%',width:`${m.loadScore}%`,background:m.loadColor,borderRadius:3 }}/>
                      </div>
                      <span style={{ fontWeight:700,color:m.loadColor,fontSize:12 }}>{m.loadScore}</span>
                    </div>
                  </td>
                  <td style={{ padding:'12px 16px' }}>
                    <span style={{ background:'#ede9fe',color:'#6d28d9',padding:'2px 8px',borderRadius:8,fontWeight:700,fontSize:11 }}>{m.projectCount}</span>
                  </td>
                  <td style={{ padding:'12px 16px',fontWeight:700,color:'#0f172a' }}>{m.byStatus.todo+m.byStatus['in-progress']}</td>
                  <td style={{ padding:'12px 16px' }}>
                    <span style={{ background:'#fef3c7',color:'#92400e',padding:'2px 8px',borderRadius:8,fontWeight:700,fontSize:11 }}>{m.byStatus['in-progress']}</span>
                  </td>
                  <td style={{ padding:'12px 16px' }}>
                    <span style={{ background:'#e0e7ff',color:'#3730a3',padding:'2px 8px',borderRadius:8,fontWeight:700,fontSize:11 }}>{m.byStatus.review}</span>
                  </td>
                  <td style={{ padding:'12px 16px',fontWeight:700,color:m.overdue>0?'#ef4444':'#22c55e' }}>{m.overdue}</td>
                  <td style={{ padding:'12px 16px',fontWeight:700,color:m.criticalCnt>2?'#ef4444':'#0f172a' }}>{m.criticalCnt}</td>
                  <td style={{ padding:'12px 16px',color:'#64748b' }}>{m.estHours}h</td>
                  <td style={{ padding:'12px 16px' }}>
                    <div style={{ display:'flex',alignItems:'center',gap:8 }}>
                      <div style={{ width:50,height:6,background:'#f1f5f9',borderRadius:3,overflow:'hidden' }}>
                        <div style={{ height:'100%',width:`${m.completionRate}%`,background:'#22c55e',borderRadius:3 }}/>
                      </div>
                      <span style={{ fontSize:11,fontWeight:700,color:'#22c55e' }}>{m.completionRate}%</span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {users.length === 0 && <div style={{ textAlign:'center',padding:40,color:'#94a3b8' }}>No team members found</div>}
        </div>
      )}

      {/* ── HEATMAP VIEW ── */}
      {view === 'heatmap' && (
        <div style={{ ...S.card,marginBottom:24 }}>
          <h3 style={{ margin:'0 0 20px',fontSize:15,fontWeight:700,color:'#0f172a' }}>Workload Heatmap</h3>
          <div style={{ overflowX:'auto' }}>
            <table style={{ width:'100%',borderCollapse:'separate',borderSpacing:4,fontSize:12 }}>
              <thead>
                <tr>
                  <th style={{ textAlign:'left',padding:'4px 8px',color:'#64748b',fontWeight:600,width:140 }}>Member</th>
                  {projects.slice(0,8).map(p=>(
                    <th key={p._id} style={{ padding:'4px 8px',color:'#64748b',fontWeight:600,textAlign:'center',maxWidth:80,overflow:'hidden' }}>
                      <div style={{ maxWidth:80,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap' }} title={p.name}>{p.name}</div>
                    </th>
                  ))}
                  <th style={{ padding:'4px 8px',color:'#64748b',fontWeight:600,textAlign:'center' }}>Total</th>
                </tr>
              </thead>
              <tbody>
                {memberData.map(m=>(
                  <tr key={m.user._id}>
                    <td style={{ padding:'4px 8px',fontWeight:600,color:'#0f172a' }}>
                      <div style={{ display:'flex',alignItems:'center',gap:8 }}>
                        <div style={{ width:24,height:24,borderRadius:'50%',background:m.loadColor,color:'#fff',display:'flex',alignItems:'center',justifyContent:'center',fontSize:10,fontWeight:800,flexShrink:0 }}>
                          {m.user.name?.charAt(0).toUpperCase()}
                        </div>
                        <span style={{ fontSize:12 }}>{m.user.name?.split(' ')[0]}</span>
                      </div>
                    </td>
                    {projects.slice(0,8).map(p=>{
                      const cnt = tasks.filter(t=>{
                        const uid = m.user._id;
                        const isAssigned = Array.isArray(t.assignedTo)
                          ? t.assignedTo.some(a=>(a?._id||a)===uid)
                          : (t.assignedTo?._id===uid||t.assignedTo===uid);
                        return isAssigned &&
                        (t.project?._id===p._id||t.project===p._id) &&
                        t.status!=='completed'
                      }).length;
                      const heat = cnt===0?'#f8fafc':cnt===1?'#dbeafe':cnt===2?'#bfdbfe':cnt<=4?'#93c5fd':cnt<=6?'#60a5fa':'#2563eb';
                      const textC = cnt>4?'#fff':'#1e40af';
                      return (
                        <td key={p._id} style={{ padding:'4px',textAlign:'center' }}>
                          <div style={{ width:52,height:36,borderRadius:6,background:heat,display:'flex',alignItems:'center',justifyContent:'center',fontWeight:700,color:cnt>0?textC:'#cbd5e0',fontSize:13,margin:'0 auto',transition:'transform .15s',cursor:'default' }}
                            title={`${m.user.name}: ${cnt} task${cnt!==1?'s':''} in ${p.name}`}>
                            {cnt > 0 ? cnt : '·'}
                          </div>
                        </td>
                      );
                    })}
                    <td style={{ padding:'4px 8px',textAlign:'center' }}>
                      <div style={{ fontWeight:800,fontSize:15,color:m.loadColor }}>{m.byStatus.todo+m.byStatus['in-progress']+m.byStatus.review}</div>
                      <div style={{ fontSize:10,color:'#7c3aed',fontWeight:600 }}>{m.projectCount} proj</div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ marginTop:16,display:'flex',alignItems:'center',gap:12,flexWrap:'wrap' }}>
            <span style={{ fontSize:12,color:'#64748b',fontWeight:600 }}>Tasks per project:</span>
            {[0,1,2,4,6].map((n,i,arr)=>(
              <div key={n} style={{ display:'flex',alignItems:'center',gap:4,fontSize:11 }}>
                <div style={{ width:20,height:14,borderRadius:3,background:['#f8fafc','#dbeafe','#bfdbfe','#60a5fa','#2563eb'][i] }}/>
                <span style={{ color:'#64748b' }}>{n===0?'0':n===1?'1':n===2?'2':n===4?'3-4':'5+'}  </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Unassigned Tasks Warning */}
      {unassigned.length > 0 && (
        <div style={{ ...S.card,border:'2px solid #fed7aa',background:'#fff7ed',marginBottom:24 }}>
          <div style={{ display:'flex',alignItems:'center',gap:10,marginBottom:16 }}>
            <FiAlertTriangle size={18} color="#f97316"/>
            <h3 style={{ margin:0,fontSize:15,fontWeight:700,color:'#c2410c' }}>
              {unassigned.length} Unassigned Task{unassigned.length>1?'s':''}
            </h3>
            <span style={{ fontSize:12,color:'#ea580c' }}>— These tasks have no owner</span>
          </div>
          <div style={{ display:'grid',gap:8 }}>
            {unassigned.slice(0,5).map(t=>(
              <Link to={`/tasks/${t._id}`} key={t._id} style={{ textDecoration:'none' }}>
                <div style={{ display:'flex',alignItems:'center',gap:10,padding:'10px 12px',background:'#fff',borderRadius:10,border:'1px solid #fed7aa' }}>
                  <div style={{ width:8,height:8,borderRadius:'50%',background:PCOL[t.priority],flexShrink:0 }}/>
                  <span style={{ flex:1,fontWeight:600,fontSize:13,color:'#0f172a' }}>{t.title}</span>
                  <span style={{ fontSize:12,color:'#94a3b8' }}>{t.project?.name}</span>
                  <span style={{ fontSize:11,padding:'2px 8px',borderRadius:8,...(SCOL[t.priority]||{}),background:PCOL[t.priority]+'18',color:PCOL[t.priority],fontWeight:700,textTransform:'uppercase' }}>
                    {t.priority}
                  </span>
                  {t.dueDate && (
                    <span style={{ fontSize:11,color:new Date(t.dueDate)<new Date()?'#ef4444':'#94a3b8',flexShrink:0 }}>
                      Due {new Date(t.dueDate).toLocaleDateString('en-IN',{day:'2-digit',month:'short'})}
                    </span>
                  )}
                </div>
              </Link>
            ))}
            {unassigned.length > 5 && (
              <div style={{ fontSize:12,color:'#ea580c',fontWeight:600,paddingLeft:4 }}>+{unassigned.length-5} more unassigned tasks</div>
            )}
          </div>
        </div>
      )}

      {/* Rebalancing Tips */}
      {overloaded > 0 && (
        <div style={{ ...S.card,border:'2px solid #e9d5ff',background:'#faf5ff' }}>
          <div style={{ display:'flex',alignItems:'center',gap:10,marginBottom:14 }}>
            <FiCheckCircle size={18} color="#7c3aed"/>
            <h3 style={{ margin:0,fontSize:15,fontWeight:700,color:'#6d28d9' }}>Rebalancing Suggestions</h3>
          </div>
          <div style={{ display:'grid',gap:8 }}>
            {memberData.filter(m=>m.loadLevel==='overloaded'||m.loadLevel==='heavy').map(heavy=>{
              const light = memberData.find(m=>m.loadLevel==='light'||m.loadLevel==='moderate');
              return (
                <div key={heavy.user._id} style={{ display:'flex',alignItems:'center',gap:10,fontSize:13,padding:'10px 12px',background:'#fff',borderRadius:10,border:'1px solid #e9d5ff' }}>
                  <div style={{ width:28,height:28,borderRadius:'50%',background:'#ef444422',color:'#ef4444',display:'flex',alignItems:'center',justifyContent:'center',fontWeight:700,fontSize:12,flexShrink:0 }}>
                    {heavy.user.name?.charAt(0)}
                  </div>
                  <span style={{ fontWeight:600,color:'#0f172a' }}>{heavy.user.name}</span>
                  <span style={{ color:'#ef4444',fontWeight:600 }}>({heavy.loadLevel})</span>
                  {light && <>
                    <span style={{ color:'#94a3b8' }}>→ move tasks to</span>
                    <div style={{ width:28,height:28,borderRadius:'50%',background:'#22c55e22',color:'#22c55e',display:'flex',alignItems:'center',justifyContent:'center',fontWeight:700,fontSize:12,flexShrink:0 }}>
                      {light.user.name?.charAt(0)}
                    </div>
                    <span style={{ fontWeight:600,color:'#0f172a' }}>{light.user.name}</span>
                    <span style={{ color:'#22c55e',fontWeight:600 }}>({light.loadLevel})</span>
                  </>}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
